import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { Box, Paper, Typography, Button, CircularProgress, IconButton } from "@mui/material";
import RoutineStyle from "../../Routine&Performance/RoutineUpload.module.css";
import FilePreviewImage from '../../../assets/images/filePreview.png';
import { getStatusStyles } from '../utils/getStatusStyles';
import { apiService } from '../../../api/apiService';
import RiskSecurityFile from '../FileUpload/RiskSecurityFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SuccessPopup from "../Popup/Success&ErrorPopup/SuccessPopup";
import ErrorPopup from "../Popup/Success&ErrorPopup/ErrorPopup";
import DeletePopup from "../Popup/Success&ErrorPopup/DeletePoup";
import FileUploadTwo from "../FileUpload/FileUploadTwo";

interface AnnualEventUploadProps {
  sectionName: string;
  management: string;
  apiPath: string;
  apiPathUpload: string;
  payloadKey?: string;
  isManager?: boolean;
  onFileUploadSuccess?: () => void;
  onViewClick: (fileData: any, sectionName: string) => void;
  fileTitle?: string;
}

const AnnualEventUpload: React.FC<AnnualEventUploadProps> = ({
  sectionName,
  management,
  apiPath,
  apiPathUpload,
  payloadKey,
  isManager,
  onFileUploadSuccess,
  onViewClick,
  fileTitle
}) => {
  const { instance } = useMsal();
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedReuploadFile, setSelectedReuploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showReuploadField, setShowReuploadField] = useState(false);
  const statusStyles = uploadedFile ? getStatusStyles(uploadedFile.status) : null;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    fetchFileData();
  }, [management, sectionName]);

  const fetchFileData = async () => {
    setLoading(true);
    try {
      const data = await apiService(apiPath, "GET", {
        management: management,
        section_name: sectionName,
      },
      undefined,
      instance as PublicClientApplication
    );
      if (data.status === "success" && data.data) {
        setUploadedFile(data.data[0]);
      } else {
        setError("No data found in API response");
      }
    } catch (error) {
      setError("Error fetching file2 data");
      console.error("Error fetching file2:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file2: File) => {
    console.log("AnnualEventUpload: File2 selected:", file2.name); // Debugging
    setSelectedFile(file2);
  };

  const handleReuploadFileSelect = (file2: File) => {
    setSelectedReuploadFile(file2);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return alert("Please select a file2");

    const formData = new FormData();
    formData.append("file", selectedFile);

    if (payloadKey) {
      formData.append(
        payloadKey,
        JSON.stringify({
          management: management,
          section_name: sectionName,
        })
      );
    }

    setUploading(true);
    try {
      const result = await apiService(apiPathUpload, "POST", formData, undefined, instance as PublicClientApplication);
      if (result.status === "success" && result.data) {
        setShowSuccess(true);
        setUploadedFile(result.data[0]);
        setSelectedFile(null);
        fetchFileData();
        if (onFileUploadSuccess) onFileUploadSuccess();
      } else {
        setErrorMessage("An error occurred during submission. Please try again.");
        setShowError(true);
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleReupload = async () => {
    if (!selectedReuploadFile) return alert("Please select a file2");

    const formData = new FormData();
    formData.append("file", selectedReuploadFile);

    setUploading(true);
    try {
      const result = await apiService(`${apiPathUpload}/${uploadedFile.id}`, "POST", formData, undefined, instance as PublicClientApplication); 
      if (result.status === "success" && result.data) {
        setShowSuccess(true);
        setUploadedFile(result.data[0]);
        setSelectedReuploadFile(null);
        setShowReuploadField(false);
        fetchFileData();
        if (onFileUploadSuccess) onFileUploadSuccess();
      } else {
        setErrorMessage("An error occurred during submission. Please try again.");
        setShowError(true);
      }
    } catch (error) {
      console.error("Reupload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!uploadedFile) return;

    setIsDeleting(true);
    try {
      const result = await apiService(`${apiPathUpload}/${uploadedFile.id}`, "DELETE", undefined, undefined, instance as PublicClientApplication); 
      if (result.status === "success") {
        setUploadedFile(null);
        setDeleteDialogOpen(false);
      } else {
        alert("File2 deletion failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReuploadClick = () => {
    setShowReuploadField(true);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showError) {
      timer = setTimeout(() => {
        setShowError(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showError]);

  return (
    <>
      <Box sx={{ typography: "body1" }}>
        <Paper elevation={0} sx={{ p: 2, textAlign: "left", backgroundColor: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }} pb={2}>
            <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>
              {sectionName}
            </Typography>
            {uploadedFile && statusStyles ? (
              <Typography
                className={RoutineStyle.pendingstatus}
                sx={{ color: statusStyles.color, backgroundColor: statusStyles.backgroundColor }}
                gutterBottom
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 5 5" fill="none">
                  <circle cx="2.5" cy="2.5" r="2.5" fill={statusStyles.svgColor} />
                </svg>
                {uploadedFile.status}
              </Typography>
            ) : null}
          </Box>

          {loading ? (
            <CircularProgress />
          ) : uploadedFile ? (
            <Box>
              <Box sx={{ cursor: 'pointer' }} onClick={() => onViewClick(uploadedFile, sectionName)}>
                <img src={FilePreviewImage} width='100%' height='155px' alt="" />
              </Box>
              <Box pt={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: '600' }} gutterBottom>
                  {uploadedFile.file_name.split("_").slice(5).join("_")}
                </Typography>
                {uploadedFile.status === "Pending" && (
                  <IconButton onClick={handleDeleteFile} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              <Typography gutterBottom>
                Created Date: {new Date(uploadedFile.created_at).toLocaleDateString()}
              </Typography>

              {["Approved", "Reverted"].includes(uploadedFile.status) && (
                <Box pt={2} pb={2}>
                  <Paper
                    elevation={1}
                    sx={{
                      textAlign: "left",
                      borderRadius: "6px",
                      border: `1px solid ${statusStyles?.color}`,
                      backgroundColor: statusStyles?.backgroundColor,
                      padding: "10px",
                    }}
                  >
                    <Typography sx={{ color: statusStyles?.color }}>
                      {uploadedFile.status === "Approved" ? "Approved Reason" : "Reverted Reason"}
                    </Typography>
                    <Typography sx={{ paddingTop: "6px" }}>
                      {uploadedFile.status === "Approved"
                        ? uploadedFile.reason_to_approve || "No reason provided."
                        : uploadedFile.reason_to_revert || "No reason provided."}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {uploadedFile.status === "Reverted" && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  {!showReuploadField ? (
                    <Button
                      variant="outlined"
                      onClick={handleReuploadClick}
                      sx={{
                        color: 'black',
                        borderColor: 'black',
                        '&:hover': {
                          borderColor: 'black',
                        },
                      }}
                    >
                      Reupload
                    </Button>
                  ) : (
                    <>
                      <FileUploadTwo
                        file2={selectedReuploadFile}
                        onFileSelect={handleReuploadFileSelect}
                        onFileRemove={() => setSelectedReuploadFile(null)}
                        fileTitle={fileTitle}

                      />
                      <Button
                        variant="outlined"
                        onClick={handleReupload}
                        disabled={uploading || !selectedReuploadFile}
                        sx={{
                          color: 'black',
                          borderColor: 'black',
                          '&:hover': {
                            borderColor: 'black',
                          },
                        }}
                      >
                        {uploading ? "Uploading..." : "Upload"}
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <>
              <FileUploadTwo
                file2={selectedFile}
                onFileSelect={handleFileSelect}
                onFileRemove={() => setSelectedFile(null)}
                fileTitle={fileTitle}

              />
              <Button
                variant="outlined"
                onClick={handleFileUpload}
                disabled={uploading || !selectedFile}
                sx={{
                  mt: 2,
                  color: 'black',
                  borderColor: 'black',
                  '&:hover': {
                    borderColor: 'black',
                  },
                }}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </>
          )}
        </Paper>
      </Box>

      <SuccessPopup
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={`${sectionName} uploaded successfully!`}
      />

      <ErrorPopup
        open={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />

      <DeletePopup
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default AnnualEventUpload;